// Copyright Caproc Oy
// System name: Aqua-Net 
// System module: Sensor
// Author: TS
//
// 19.3.2020 TS Initial version
// 7.1.2024 1.0.8 TS Polygon texts and titles
// 7.4.2026 TS (claude) Append location_address (and city) in parentheses after sensor group name in the table when set
// 7.4.2026 TS (claude) Added Description column between group name and sensor count in SensorGroups table
// 8.4.2026 TS (claude) Sort sensor groups in table: Default group first, rest alphabetically (fi locale)
// 30.7.2026 TS (claude) Hide the group delete button/column from non-admins: backend requires role A/S/G since 1.43.0, so other roles only got an unexplained 403


import React from "react";
import { Message, Header, Button, Table, Segment, Dimmer, Loader, Image, Confirm, Input } from "semantic-ui-react";
import Shell from "./Shell";
import { authAxios } from "../../utils";
import { setActivity } from "../../store/actions/auth";
import { withRouter, Link, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import { withTranslation } from 'react-i18next';
import { fetchSensorGroupsURL, deleteSensorGroupDetailsURL } from "../../constants";


class SensorGroups extends React.Component {
    state = {
        data: null,
        delete_confirm_open: false,
        delete_confirm_text: "",
        delete_item: "",
        error: null,
        loading: false,
        search: ""
    };

    componentDidMount() {
        this.props.setActivity();
        this.handleFetchList();
    }

    handleFetchList = () => {
        this.setState({
            loading: true
        });
        const { activeCompanyId } = this.props;
        authAxios
            .get(fetchSensorGroupsURL(activeCompanyId))
            .then(res => {
                this.setState({
                    data: res.data,
                    error: null,
                    loading: false
                });
            })
            .catch(err => {
                this.setState({
                    loading: false,
                    error: err
                });
            });
    };

    handleFormatList = data => {
        let i = 0;
        let newlist = [];
        for (i in data) {
            newlist.push(data[i]);
        }
        return newlist;
    };

    groupDetails = id => {
        this.props.history.push(`/sensor/sensor-group-details/${id}/`)
    };

    openDeleteConfirm = (text, delete_item) => {
        this.setState({
            delete_confirm_open: true,
            delete_confirm_text: text,
            delete_item: delete_item
        });
    };

    closeDeleteConfirm = () => {
        this.setState({
            delete_confirm_open: false,
            delete_confirm_text: "",
            delete_item: ""
        });
    };

    handleDelete = id => {
        this.setState({
            loading: true
        });
        const { activeCompanyId } = this.props;
        authAxios
            .delete(deleteSensorGroupDetailsURL(activeCompanyId, id))
            .then(res => {
                this.setState({
                    error: null,
                    loading: false
                });
                this.handleFetchList();
            })
            .catch(err => {
                this.setState({
                    loading: false,
                    error: err
                });
            });
        this.setState({
            delete_confirm_open: false,
            delete_confirm_text: "",
            delete_item: ""
        });
    };

    handleChange = e => {
        this.setState({
            [e.target.name]: e.target.value
        });
    };

    render() {
        const { data, error, loading, delete_confirm_open, delete_confirm_text, delete_item, search } = this.state;
        const { isAuthenticated, activeRoles, t } = this.props;
        // Backend permission for group delete is role A/S/G (IsCompanyAdminOrGroupSuperuser)
        const canDeleteGroups = activeRoles && (activeRoles.includes("A") || activeRoles.includes("S") || activeRoles.includes("G"));
        if (!isAuthenticated) {
            return <Redirect to="/login" />;
        }
        // console.log(this.state);
        let filter_data = [];

        // Filter data for search
        if (data !== null) {
            filter_data = data.filter((group) => {
                return group.name.toLowerCase().indexOf(search.toLocaleLowerCase()) !== -1 || group.description.toLowerCase().indexOf(search.toLocaleLowerCase()) !== -1;
            })
            // Default group always first, rest alphabetically (fi locale)
            filter_data = filter_data.slice().sort((a, b) => {
                if (a.name === "Default group") return -1;
                if (b.name === "Default group") return 1;
                return (a.name || "").localeCompare(b.name || "", "fi");
            });
        }
        
        const logo = localStorage.getItem("Logo");
        return (
            <Shell>
                {logo && (
                    <>
                        {logo === "Polygon" ? (
                            <Header as="h3">{t("Luo / Poista kohteita")}</Header>
                        ) : (
                            <Header as="h3">{t("Sensor Groups")}</Header>
                        )}
                        
                        <Link to="/sensor/sensor-group-details/create/">
                            <Button floated="right" color="green">
                                {logo === "Polygon" ? (
                                    t("+ Luo kohde")
                                ) : (
                                    t("+ Sensor Group")
                                )}
                            </Button>
                        </Link>

                        {error && (
                            <Message
                                error
                                header={t("There was an error")}
                                content={JSON.stringify(error)}
                            />
                        )}
                        {loading && (
                            <Segment>
                                <Dimmer active inverted>
                                    <Loader inverted>Loading</Loader>
                                </Dimmer>
                                <Image src="https://react.semantic-ui.com/images/wireframe/short-paragraph.png" />
                            </Segment>
                        )}
                        {data && (
                            <React.Fragment>
                                <Input
                                    icon='search'
                                    value={search}
                                    name="search"
                                    onChange={this.handleChange} />
                                <Table striped selectable celled>
                                    <Table.Header>
                                        <Table.Row>
                                            {logo === "Polygon" ? (
                                                <>
                                                    <Table.HeaderCell>{t("Kohteet")}</Table.HeaderCell>
                                                    <Table.HeaderCell>{t("Description")}</Table.HeaderCell>
                                                    <Table.HeaderCell>{t("Antureita kohteessa (kpl)")}</Table.HeaderCell>
                                                    {canDeleteGroups && <Table.HeaderCell />}
                                                </>
                                            ) : (
                                                <>
                                                    <Table.HeaderCell>{t("Group name")}</Table.HeaderCell>
                                                    <Table.HeaderCell>{t("Description")}</Table.HeaderCell>
                                                    <Table.HeaderCell>{t("Sensors in group (pcs)")}</Table.HeaderCell>
                                                    {canDeleteGroups && <Table.HeaderCell />}
                                                </>
                                            )}
                                        </Table.Row>
                                    </Table.Header>

                                    <Table.Body>
                                        {filter_data.map((group, key) => {
                                            return (
                                                <Table.Row key={key} >
                                                    {(group.name === "Default group" && logo === "Polygon") ? (
                                                        <Table.Cell onClick={() => this.groupDetails(group.id)}>
                                                            {t("TMP VARASTO")}
                                                            {group.location_address && (<> ({group.location_address}{group.location_city ? `, ${group.location_city}` : ""})</>)}
                                                        </Table.Cell>
                                                    ) : (
                                                        <Table.Cell onClick={() => this.groupDetails(group.id)}>
                                                            {t(group.name)}
                                                            {group.location_address && (<> ({group.location_address}{group.location_city ? `, ${group.location_city}` : ""})</>)}
                                                        </Table.Cell>
                                                    )}
                                                    <Table.Cell onClick={() => this.groupDetails(group.id)}>{group.description}</Table.Cell>
                                                    <Table.Cell onClick={() => this.groupDetails(group.id)}>{group.sensors.length}</Table.Cell>
                                                    {/* Default group should not be deleted */}
                                                    {(canDeleteGroups && group.name !== "Default group") && (
                                                        <>
                                                            {logo === "Polygon" ? (
                                                                <Table.Cell collapsing textAlign='center'><Button compact negative size='mini' icon='remove'
                                                                    onClick={() => this.openDeleteConfirm(t("Haluatko poistaa kohteen? Poistetun kohteen sensorit siirtyvät varastoon."), group.id)} /></Table.Cell>
                                                            ) : (
                                                                <Table.Cell collapsing textAlign='center'><Button compact negative size='mini' icon='remove'
                                                                    onClick={() => this.openDeleteConfirm(t("Are you sure your want to delete", { name: group.name }), group.id)} /></Table.Cell>
                                                            )}
                                                        </>
                                                    )}
                                                </Table.Row>
                                            );
                                        })}
                                    </Table.Body>
                                </Table>
                            </React.Fragment>
                        )}
                        <Confirm
                            content={delete_confirm_text}
                            open={delete_confirm_open}
                            onCancel={() => this.closeDeleteConfirm()}
                            onConfirm={() => this.handleDelete(delete_item)}
                        />
                    </>
                )}
            </Shell>
        );
    }
}
const mapStateToProps = state => {
    return {
        activeCompanyId: state.company.activeCompanyId,
        activeRoles: state.company.activeRoles,
        isAuthenticated: state.auth.token !== null
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setActivity: () => dispatch(setActivity())
    };
};

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(withTranslation('sensorgroups')(SensorGroups))
);